import React, { useCallback, useEffect, useState } from 'react';
import { Target, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const STATUS = ['Met', 'Progressing', 'Not met', 'Revised'];
const TONE = { Met: 'bg-green-100 text-green-800', Progressing: 'bg-blue-100 text-blue-800', 'Not met': 'bg-red-100 text-red-800', Revised: 'bg-amber-100 text-amber-800' };
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const us = (d) => (d ? `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}` : '');

const GoalRow = ({ g, patientId, onSaved }) => {
  const [status, setStatus] = useState('Progressing');
  const [note, setNote] = useState('');
  const [date, setDate] = useState(today());
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/therapy-goals/${patientId}/progress`, { goalId: g.id, status, note, visitDate: date }); toast.success('Goal progress saved'); setNote(''); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save progress'); } finally { setBusy(false); }
  };
  return (
    <li className="border border-[#c7d4f5] rounded-lg p-3 bg-white" data-testid={`goal-${g.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-sm text-gray-800"><span className="text-xs font-semibold text-[#1e40af] mr-2">{g.term}</span>{g.text}</p>
        {g.latest ? <span className={`text-xs px-2 py-0.5 rounded-full ${TONE[g.latest.status]}`} data-testid={`goal-status-${g.id}`}>{g.latest.status} · {us(g.latest.visitDate)}</span>
          : <span className="text-xs text-gray-400" data-testid={`goal-status-${g.id}`}>No progress recorded</span>}
      </div>
      {g.history.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-gray-600" data-testid={`goal-history-${g.id}`}>
          {g.history.map(h => <li key={h.id}><span className="font-medium">{us(h.visitDate)}</span> — {h.status}{h.note ? `: ${h.note}` : ''}</li>)}
        </ul>
      )}
      {open ? (
        <div className="mt-2 flex flex-wrap gap-2 items-center">
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="border rounded px-2 py-1 text-xs" data-testid={`goal-date-${g.id}`} />
          <select value={status} onChange={e => setStatus(e.target.value)} className="border rounded px-2 py-1 text-xs" data-testid={`goal-status-select-${g.id}`}>
            {STATUS.map(s => <option key={s}>{s}</option>)}
          </select>
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Short note for this visit" className="flex-1 min-w-[12rem] border rounded px-2 py-1 text-xs" data-testid={`goal-note-${g.id}`} />
          <button onClick={save} disabled={busy || !date} className="px-3 py-1 rounded bg-[#1e40af] text-white text-xs disabled:opacity-50" data-testid={`goal-save-${g.id}`}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      ) : <button onClick={() => setOpen(true)} className="mt-2 text-xs text-[#1e40af] hover:underline" data-testid={`goal-record-${g.id}`}>Record progress for a visit</button>}
    </li>
  );
};

// Short- and long-term goals from the saved PT/OT/ST evaluations, with status at every visit
export const TherapyGoalTracker = ({ patientId, discipline }) => {
  const [goals, setGoals] = useState(null);
  const load = useCallback(() => {
    api.get(`/therapy-goals/${patientId}`).then(r => setGoals(r.data.goals || [])).catch(() => setGoals([]));
  }, [patientId]);
  useEffect(() => { if (patientId) load(); }, [patientId, load]);
  if (!patientId) return null;
  const list = (goals || []).filter(g => !discipline || g.discipline === discipline);
  const groups = [...new Set(list.map(g => g.disciplineName))];
  return (
    <section className="bg-[#f5f8ff] rounded-lg border border-[#c7d4f5] p-4" data-testid="therapy-goal-tracker">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-[#1e40af] mb-3"><Target className="w-4 h-4" /> Therapy goal tracker</h2>
      {goals === null ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        : !list.length ? <p className="text-xs text-gray-500" data-testid="therapy-goals-empty">No goals yet. Enter short- and long-term goals on the evaluation and save it.</p>
        : groups.map(name => (
          <div key={name} className="mb-3">
            <h3 className="text-xs font-semibold text-gray-700 mb-2">{name} <span className="font-normal text-gray-400">({list.find(g => g.disciplineName === name).source})</span></h3>
            <ul className="space-y-2">{list.filter(g => g.disciplineName === name).map(g => <GoalRow key={g.id} g={g} patientId={patientId} onSaved={load} />)}</ul>
          </div>
        ))}
    </section>
  );
};

export default TherapyGoalTracker;
